import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import { LoadingSpinner } from '../common';

interface User {
  id: string;
  email: string;
  name?: string;
  roles: string[];
  status: 'active' | 'inactive';
}

interface Role {
  roleId: string;
  name: string;
  description: string;
}

interface UserEditModalProps {
  user: User;
  roles: Role[];  // Roles the current user can assign
  allRoles?: Role[];  // All roles (to display non-manageable roles as read-only)
  rolesLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedUser: User) => Promise<void>;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  roles,
  allRoles,
  rolesLoading,
  isOpen,
  onClose,
  onSave,
}) => {
  const [editedUser, setEditedUser] = useState<User>(user);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEditedUser(user);
    setError('');
  }, [user]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    
    try {
      await onSave(editedUser);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 text-sm text-red-700 bg-red-100 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={editedUser.email}
                onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={editedUser.name || ''}
                onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles
              </label>
              {rolesLoading ? (
                <div className="flex items-center justify-center py-2">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                  {/* Show non-manageable roles that the user has (read-only) */}
                  {allRoles && editedUser.roles
                    .filter(roleName => !roles.some(r => r.name === roleName))
                    .map(roleName => {
                      const roleInfo = allRoles.find(r => r.name === roleName);
                      if (!roleInfo) return null;
                      return (
                        <div
                          key={roleInfo.roleId}
                          className="flex items-start p-2 rounded bg-gray-100 opacity-75"
                          title="You cannot modify this role"
                        >
                          <input
                            type="checkbox"
                            checked={true}
                            disabled={true}
                            className="mt-1 h-4 w-4 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-700">
                              {roleInfo.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              <span className="ml-2 text-xs text-gray-500">(cannot modify)</span>
                            </span>
                            <p className="text-xs text-gray-500">{roleInfo.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  {/* Show manageable roles */}
                  {roles.map((role) => {
                    const isChecked = editedUser.roles.includes(role.name);
                    return (
                      <label
                        key={role.roleId}
                        className={`flex items-start p-2 rounded cursor-pointer transition-colors ${
                          isChecked ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (isSaving) return;
                            const newRoles = e.target.checked
                              ? [...editedUser.roles, role.name]
                              : editedUser.roles.filter(r => r !== role.name);
                            setEditedUser({ ...editedUser, roles: newRoles });
                          }}
                          className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          disabled={isSaving}
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-900">
                            {role.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <p className="text-xs text-gray-500">{role.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {!rolesLoading && editedUser.roles.length === 0 && (
                <p className="mt-1 text-sm text-red-600">At least one role is required</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSaving || rolesLoading || editedUser.roles.length === 0}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEditModal;